import { useState, useMemo } from "react";
import { CheckCircle2, AlertTriangle, AlertOctagon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ReportFreshnessModal } from "@/components/report-freshness-modal";
import { useReportFreshness, type ReportType } from "@/hooks/use-report-freshness";

const CRITICAL_STALE_DAYS = 2;

interface ReportFreshnessBadgeProps {
  reportTypes: ReportType[];
  testIdPrefix?: string;
}

export function ReportFreshnessBadge({
  reportTypes,
  testIdPrefix = "freshness",
}: ReportFreshnessBadgeProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const { allPets, interaction, dataStartDate, getStaleStatus, isLoading } = useReportFreshness();

  const status = useMemo(() => {
    return getStaleStatus(reportTypes);
  }, [getStaleStatus, reportTypes]);

  const isCriticallyStale = useMemo(() => {
    if (!reportTypes.includes("allPets")) return false;
    return allPets.staleLocations.some((loc) => loc.staleDays >= CRITICAL_STALE_DAYS);
  }, [allPets, reportTypes]);

  const allLocationsForModal = useMemo(() => {
    if (reportTypes.includes("allPets")) return allPets.locations;
    if (reportTypes.includes("interaction")) return interaction.locations;
    return [];
  }, [allPets.locations, interaction.locations, reportTypes]);

  const isBothTypes = reportTypes.includes("allPets") && reportTypes.includes("interaction");

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground hidden sm:inline">Data Status</span>
        <Skeleton
          className="h-6 w-16 sm:w-24"
          data-testid={`skeleton-${testIdPrefix}-badge`}
        />
      </div>
    );
  }

  const totalIssues = status.staleCount + status.gapCount;

  if (status.isFresh) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground hidden sm:inline">Data Status</span>
        <Badge
          variant="outline"
          className="text-emerald-600 dark:text-emerald-500 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 cursor-default"
          data-testid={`badge-${testIdPrefix}-fresh`}
        >
          <CheckCircle2 className="h-3.5 w-3.5 sm:mr-1 flex-shrink-0" />
          <span className="whitespace-nowrap hidden sm:inline">Up to date</span>
        </Badge>
      </div>
    );
  }

  const badgeText =
    status.staleCount > 0
      ? `${status.staleCount} stale`
      : `${status.gapCount} with gaps`;

  const BadgeIcon = isCriticallyStale ? AlertOctagon : AlertTriangle;
  const badgeColors = isCriticallyStale
    ? "text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/30"
    : "text-amber-600 dark:text-amber-500 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30";

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground hidden sm:inline">Data Status</span>
      <Badge
        variant="outline"
        className={`${badgeColors} cursor-pointer hover-elevate`}
        onClick={() => setModalOpen(true)}
        data-testid={`badge-${testIdPrefix}-stale`}
      >
        <BadgeIcon className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
        <span className="hidden sm:inline whitespace-nowrap">{badgeText}</span>
        <span className="sm:hidden">{totalIssues}</span>
      </Badge>

      <ReportFreshnessModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        staleLocations={status.staleLocations}
        locationsWithGaps={status.locationsWithGaps}
        reportTypes={reportTypes}
        isCriticallyStale={isCriticallyStale}
        allLocations={isBothTypes ? undefined : allLocationsForModal}
        allPetsLocations={isBothTypes ? allPets.locations : undefined}
        interactionLocations={isBothTypes ? interaction.locations : undefined}
        dataStartDate={dataStartDate}
      />
    </div>
  );
}
